import { formatMoney, getAccountMoney } from '../../../shared/money/exact-money.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ProfileVerificationTab from '../../../pages/profiles/components/profile-verification-tab.jsx';
import VerificationAccessCard from '../../verification-settings/ui/verification-access-card.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  isInvalidSecurityStepUpCodeError,
  isSecurityStepUpRequiredError,
  isSensitiveActionRestrictedError,
} from '../../../lib/admin-api/http-client.js';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import DocumentDetailsModal from './document-details-modal.jsx';
import ProfileEmailChangeModal from './profile-email-change-modal.jsx';
import ProfileEmailRevealModal from './profile-email-reveal-modal.jsx';
import SecurityStepUpModal from './security-step-up-modal.jsx';
import ProfileIdentityContactTab from './profile-identity-contact-tab.jsx';
import { useCalls } from '../../calls/context/calls-context.jsx';
import CallsPage from '../../calls/ui/calls-page.jsx';
import { useInternalMailCreate } from '../../internal-mail/hooks/use-internal-mail-create.js';
import InternalMailCreateModal from '../../internal-mail/ui/internal-mail-create-modal.jsx';
import { useComplianceMode } from '../../system-navigation/context/compliance-mode-context.jsx';
import TradesTable from '../../trades/ui/trades-table.jsx';
import ProfilePartnershipModal from './profile-partnership-modal.jsx';
import { buildLegalAcceptanceRows } from '../model/profile-details-compliance.model.js';
import { isPendingVerificationAccount } from '../../accounts/model/account-platform-state.js';

const DOCUMENT_STATUS_OPTIONS = [
  'new',
  'approved',
  'rejected',
  'pending',
];

function getDocumentStatusBadgeClass(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  const map = {
    approved: 'bg-success-subtle text-success',
    verified: 'bg-success-subtle text-success',
    pending: 'bg-warning-subtle text-warning',
    review: 'bg-warning-subtle text-warning',
    rejected: 'bg-danger-subtle text-danger',
    declined: 'bg-danger-subtle text-danger',
    expired: 'app-badge-disabled',
    uploaded: 'bg-info-subtle text-info',
    submitted: 'bg-primary-subtle text-primary',
    new: 'bg-primary-subtle text-primary',
  };

  return map[status] || 'app-badge-disabled';
}

function getDocumentTypeBadgeClass(typeValue) {
  const type = String(typeValue || '').trim().toLowerCase();
  const map = {
    'id-front': 'bg-primary-subtle text-primary',
    'id-back': 'bg-primary-subtle text-primary',
    poa: 'bg-info-subtle text-info',
    other: 'app-badge-disabled',
  };

  return map[type] || 'app-badge-disabled';
}

function getAccountTypeBadgeClass(typeValue) {
  const type = String(typeValue || '').trim().toLowerCase();
  const map = {
    live: 'bg-success-subtle text-success',
    demo: 'bg-info-subtle text-info',
    wallet: 'bg-warning-subtle text-warning',
  };

  return map[type] || 'bg-secondary-subtle text-secondary';
}

function PendingVerificationBadge({ label }) {
  return (
    <span className="badge rounded-pill bg-warning-subtle text-warning border border-warning-subtle">
      {label}
    </span>
  );
}

function isTrueLike(value) {
  if (value === true || value === 1) {
    return true;
  }

  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
}

function getTranslationBadgeMeta(document) {
  const translationRequired = pickFirst(document?.translationRequired, document?.translation_required);
  if (isTrueLike(translationRequired)) {
    return { label: 'PENDING', className: 'bg-warning-subtle text-warning' };
  }

  const translation = String(pickFirst(document?.translation) || '').trim();
  if (translation) {
    return { label: 'DONE', className: 'bg-success-subtle text-success' };
  }

  return { label: 'N/A', className: 'app-badge-disabled' };
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function extractAccountSync(account) {
  return (
    account?.sync ||
    account?.tpAccountSync ||
    account?.tpAccountSyncDTO ||
    account?.tp_account_sync ||
    account?.tp_account_sync_dto ||
    null
  );
}

function pickSyncField(sync, ...keys) {
  return pickFirst(...keys.map((key) => sync?.[key]));
}

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

function formatDateInput(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 10);
}

function createSecurityRequestId() {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `admin-step-up-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function triggerBlobDownload(response, fallbackName) {
  const objectUrl = URL.createObjectURL(response.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = response.filename || fallbackName || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function formatBooleanLabel(value) {
  return value === true ? 'Yes' : value === false ? 'No' : '-';
}

function getStatusBadgeClass(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();

  if (['verified', 'active'].includes(status)) {
    return 'bg-success-subtle text-success';
  }

  if (['pending', 'pending_verification', 'limited'].includes(status)) {
    return 'bg-warning-subtle text-warning';
  }

  if (['closed', 'blocked', 'disabled', 'rejected'].includes(status)) {
    return 'bg-danger-subtle text-danger';
  }

  return 'app-badge-disabled';
}

function getTransactionStatusBadgeClass(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();

  if (['processed', 'succeed'].includes(status)) {
    return 'bg-success-subtle text-success';
  }

  if (['created', 'pending', 'resolution', 'pending_input'].includes(status)) {
    return 'bg-warning-subtle text-warning';
  }

  if (['failed', 'canceled', 'declined', 'rejected'].includes(status)) {
    return 'bg-danger-subtle text-danger';
  }

  return 'app-badge-disabled';
}

function getMessageTypeBadgeClass(typeValue) {
  return String(typeValue || '').trim().toLowerCase() === 'marketing'
    ? 'bg-warning-subtle text-warning'
    : 'bg-info-subtle text-info';
}

function getSuitabilityBadgeClass(statusValue, resultValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  const result = String(resultValue || '').trim().toLowerCase();

  if (status === 'acknowledged' || result === 'suitable') {
    return 'bg-success-subtle text-success';
  }

  if (status === 'overridden') {
    return 'bg-info-subtle text-info';
  }

  if (result === 'requires_acknowledgement' || status === 'pending') {
    return 'bg-warning-subtle text-warning';
  }

  if (result === 'not_suitable') {
    return 'bg-danger-subtle text-danger';
  }

  return 'app-badge-disabled';
}

function getAuditBadgeClass(isValid) {
  if (isValid === true) {
    return 'bg-success-subtle text-success';
  }

  if (isValid === false) {
    return 'bg-danger-subtle text-danger';
  }

  return 'app-badge-disabled';
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeKycSteps(payload) {
  const candidates = [
    payload?.steps,
    payload?.items,
    payload?.data?.steps,
    payload?.data?.items,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate);
    if (list.length > 0) {
      return list;
    }
  }

  return [];
}

function parseMaybeJson(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const raw = value.trim();
  if (!raw) {
    return '';
  }

  const looksLikeJson = (raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'));
  if (!looksLikeJson) {
    return value;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return value;
  }
}

function formatKycValue(value) {
  const parsed = parseMaybeJson(value);

  if (parsed === undefined || parsed === null || parsed === '') {
    return '';
  }

  if (typeof parsed === 'string') {
    return parsed;
  }

  if (typeof parsed === 'number' || typeof parsed === 'boolean') {
    return String(parsed);
  }

  if (Array.isArray(parsed)) {
    if (parsed.every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
      return parsed.map((item) => String(item)).join(', ');
    }

    return JSON.stringify(parsed, null, 2);
  }

  if (typeof parsed === 'object') {
    return JSON.stringify(parsed, null, 2);
  }

  return String(parsed);
}

function humanizeActionLogDetailKey(key) {
  const acronyms = new Set(['id', 'ip', 'uid', 'url', 'api', 'kyc', 'ui']);

  return String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((part) => {
      const normalized = part.toLowerCase();
      return acronyms.has(normalized)
        ? normalized.toUpperCase()
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ') || '-';
}

function formatActionLogPrimitive(value) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(value);
}

function renderActionLogDetailValue(value) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (['string', 'number', 'boolean'].includes(typeof value)) {
    return formatActionLogPrimitive(value);
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '-';
    }

    if (value.every((item) => item === null || ['string', 'number', 'boolean'].includes(typeof item))) {
      return value.map((item) => formatActionLogPrimitive(item)).join(', ');
    }

    return (
      <ol className="app-action-log-details-list mb-0">
        {value.map((item, index) => (
          <li key={`${index}-${typeof item}`}>
            {renderActionLogDetailValue(item)}
          </li>
        ))}
      </ol>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) {
      return '-';
    }

    return (
      <dl className="app-action-log-details mb-0">
        {entries.map(([key, entryValue]) => (
          <div className="app-action-log-details-row" key={key}>
            <dt>{humanizeActionLogDetailKey(key)}</dt>
            <dd>{renderActionLogDetailValue(entryValue)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return String(value);
}

function ActionLogDetails({ details }) {
  const parsed = parseMaybeJson(details);

  return renderActionLogDetailValue(parsed);
}

function buildKycSettingsMap(stepsSettings) {
  const map = new Map();

  normalizeList(stepsSettings).forEach((step, stepIndex) => {
    const stepId = String(pickFirst(step?.uid, step?.id, step?.stepId, `step-${stepIndex}`));
    const fieldsMap = new Map();

    normalizeList(step?.fields).forEach((field, fieldIndex) => {
      const fieldId = String(pickFirst(field?.uid, field?.id, field?.fieldId, `field-${fieldIndex}`));
      fieldsMap.set(fieldId, {
        name: pickFirst(field?.name, field?.label, fieldId) || fieldId,
      });
    });

    map.set(stepId, {
      name: pickFirst(step?.name, step?.description, stepId) || stepId,
      fieldsMap,
    });
  });

  return map;
}

function buildKycStepRows(kycData, settingsSteps) {
  const settingsMap = buildKycSettingsMap(settingsSteps);

  return normalizeKycSteps(kycData).map((step, stepIndex) => {
    const stepId = String(pickFirst(step?.stepId, step?.uid, step?.id, `step-${stepIndex}`));
    const stepSettings = settingsMap.get(stepId);
    const stepName = pickFirst(step?.stepName, step?.step_name, step?.name, stepSettings?.name, `Step ${stepIndex + 1}`);

    const fields = normalizeList(step?.items).map((item, index) => {
      const fieldId = String(pickFirst(item?.fieldId, item?.uid, item?.id, `field-${index}`));
      const fieldSettings = stepSettings?.fieldsMap?.get(fieldId);
      const fieldName = pickFirst(
        item?.fieldName,
        item?.field_name,
        item?.name,
        fieldSettings?.name,
        fieldId,
      ) || fieldId;

      return {
        id: fieldId,
        name: fieldName,
        value: formatKycValue(item?.data),
      };
    });

    return {
      id: stepId,
      name: stepName,
      fields,
    };
  });
}

function formatPersonName(person) {
  if (!person || typeof person !== 'object') {
    return '-';
  }

  const firstName = pickFirst(person.firstName, person.first_name);
  const lastName = pickFirst(person.lastName, person.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || pickFirst(person.name, person.full_name, person.username, person.emailMasked, person.email_masked) || '-';
}

function ProfileDetailsPageView({
  profileId,
  tabs,
  activeTab,
  setActiveTab,
  token,
  profile,
  canUpdateProfile,
  canViewExternalVerifications,
  reloadVerificationProfile,
  canRevealProfilePii,
  profileEditForm,
  profileEditErrors,
  isProfileSaving,
  profileSaveError,
  profileSaveSuccess,
  profileEmailErrors,
  isProfileEmailSaving,
  profileEmailRevealErrors,
  isProfileEmailRevealSaving,
  revealedEmail,
  profilePhoneRevealErrors,
  isProfilePhoneRevealSaving,
  revealedPhone,
  updateProfileEditField,
  resetProfileEditForm,
  resetProfileEmailErrors,
  saveProfileDetails,
  saveProfileEmail,
  revealProfileEmail,
  revealProfilePhone,
  isLoading,
  error,
  accounts,
  isAccountsLoading,
  accountsError,
  profileTransactionsRows = [],
  isProfileTransactionsLoading = false,
  isProfileTransactionsLoadingMore = false,
  hasMoreProfileTransactions = false,
  profileTransactionsError = '',
  profileTransactionFilters = {
    type: '',
    method: '',
    status: '',
  },
  profileTransactionStatusOptions = [],
  profileTransactionSort = {
    sortBy: 'created',
    sortOrder: 'desc',
  },
  updateProfileTransactionFilter,
  applyProfileTransactionFilters,
  clearProfileTransactionFilters,
  changeProfileTransactionSort,
  loadMoreProfileTransactions,
  partnership,
  isPartnershipLoading,
  partnershipError,
  isPartnershipModalOpen,
  partnershipModalAccount,
  partnershipPartnerQuery,
  setPartnershipPartnerQuery,
  partnershipSearchResults,
  isPartnershipSearchLoading,
  partnershipSearchError,
  selectedPartnershipPartner,
  isPartnershipSaving,
  partnershipSaveError,
  openPartnershipModal,
  closePartnershipModal,
  selectPartnershipPartner,
  savePartnershipPartner,
  unlinkPartnershipPartner,
  syncingAccountId,
  accountSyncError,
  accountSyncSuccess,
  syncAccount,
  kyc,
  isKycLoading,
  kycError,
  kycSettings,
  isKycSettingsLoading,
  kycSettingsError,
  documents,
  isDocumentsLoading,
  documentsError,
  refreshProfileDocuments,
  actionLogs,
  isActionLogsLoading,
  actionLogsError,
  profileMail,
  isProfileMailLoading,
  profileMailError,
  refreshProfileMail,
  suitabilityAssessments,
  isSuitabilityLoading,
  suitabilityError,
  isCreatingSuitability,
  isUpdatingVulnerability,
  updateVulnerability,
  createSuitabilityAssessment,
  auditIntegrity,
  isAuditChecking,
  checkAuditIntegrity,
  isVerifyingProfile,
  isRequestingInformation,
  isInvalidatingProfile,
  isValidatingProfile,
  verifyProfileError,
  verifyProfileSuccess,
  verifyProfile,
  requestProfileInformation,
  invalidateProfile,
  validateProfile,
  isAnonymizingProfile,
  isExportingProfile,
  complianceError,
  complianceSuccess,
  anonymizeProfile,
  exportProfileData,
  securityStepUp,
  submitSecurityStepUpCode,
  cancelSecurityStepUp,
  trades,
  tradesTotal,
  tradesTotalLots,
  tradesTotalProfit,
  tradesProfitCurrency,
  isTradesLoading,
  tradesError,
  tradesSymbolFilter,
  tradesDateFromFilter,
  tradesDateToFilter,
  handleTradesSymbolChange,
  handleTradesDateFromChange,
  handleTradesDateToChange,
  loadMoreTrades,
}) {
  const { t, i18n } = useTranslation();
  const calls = useCalls();
  React.useEffect(() => {
    if (activeTab === 'calls' && !calls.canView) setActiveTab('profile');
  }, [activeTab, calls.canView, setActiveTab]);
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const { isFinancialRegulation } = useComplianceMode();
  const location = useLocation();
  const rawProfile = profile?.source || null;
  const partnershipData = {
    historyPartners: [],
    accounts: [],
    ...(partnership || {}),
  };
  const kycStepRows = buildKycStepRows(kyc, kycSettings);
  const legalAcceptanceRows = buildLegalAcceptanceRows(profile?.legalAcceptances);
  const [documentsState, setDocumentsState] = React.useState(documents);
  const [selectedDocumentId, setSelectedDocumentId] = React.useState('');
  const [documentViewer, setDocumentViewer] = React.useState({
    isLoading: false,
    error: '',
    url: '',
    mime: '',
  });
  const [documentSecurityStepUp, setDocumentSecurityStepUp] = React.useState({
    isOpen: false,
    isSubmitting: false,
    error: '',
  });
  const documentSecurityStepUpRef = React.useRef(null);
  const [documentForm, setDocumentForm] = React.useState({
    translation: '',
    expirationDate: '',
    status: '',
    translationRequired: false,
  });
  const [isDocumentSaving, setIsDocumentSaving] = React.useState(false);
  const [documentSaveError, setDocumentSaveError] = React.useState('');
  const [documentSaveSuccess, setDocumentSaveSuccess] = React.useState('');
  const [isDocumentDownloading, setIsDocumentDownloading] = React.useState(false);
  const [documentDownloadError, setDocumentDownloadError] = React.useState('');
  const [isCreateMailOpen, setIsCreateMailOpen] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [isEmailRevealModalOpen, setIsEmailRevealModalOpen] = React.useState(false);
  const [isPhoneRevealModalOpen, setIsPhoneRevealModalOpen] = React.useState(false);
  const [emailForm, setEmailForm] = React.useState({ email: '', reason: '' });
  const [emailRevealForm, setEmailRevealForm] = React.useState({ reason: '' });
  const [phoneRevealForm, setPhoneRevealForm] = React.useState({ reason: '' });
  const [vulnerabilityForm, setVulnerabilityForm] = React.useState({
    flag: false,
    notes: '',
  });
  const [suitabilityForm, setSuitabilityForm] = React.useState({
    result: 'suitable',
    override: false,
    notes: '',
    answersText: '',
  });
  const [suitabilityFormError, setSuitabilityFormError] = React.useState('');

  const handleMailCreated = React.useCallback(() => {
    setIsCreateMailOpen(false);
    if (refreshProfileMail) {
      refreshProfileMail();
    }
  }, [refreshProfileMail]);

  const createMailHook = useInternalMailCreate({
    prefilledProfileUid: profileId || null,
    onSuccess: handleMailCreated,
  });

  const handleOpenCreateMail = React.useCallback(() => {
    createMailHook.resetForm();
    setIsCreateMailOpen(true);
  }, [createMailHook]);

  const openEmailModal = React.useCallback(() => {
    resetProfileEmailErrors();
    setEmailForm({ email: revealedEmail || '', reason: '' });
    setIsEmailModalOpen(true);
  }, [resetProfileEmailErrors, revealedEmail]);

  const closeEmailModal = React.useCallback(() => {
    if (!isProfileEmailSaving) {
      setIsEmailModalOpen(false);
    }
  }, [isProfileEmailSaving]);

  const changeEmailForm = React.useCallback((field, value) => {
    setEmailForm((current) => ({ ...current, [field]: value }));
  }, []);

  const openEmailRevealModal = React.useCallback(() => {
    setEmailRevealForm({ reason: '' });
    setIsEmailRevealModalOpen(true);
  }, []);

  const closeEmailRevealModal = React.useCallback(() => {
    if (!isProfileEmailRevealSaving) {
      setIsEmailRevealModalOpen(false);
    }
  }, [isProfileEmailRevealSaving]);

  const changeEmailRevealForm = React.useCallback((field, value) => {
    setEmailRevealForm((current) => ({ ...current, [field]: value }));
  }, []);

  const openPhoneRevealModal = React.useCallback(() => {
    setPhoneRevealForm({ reason: '' });
    setIsPhoneRevealModalOpen(true);
  }, []);

  const closePhoneRevealModal = React.useCallback(() => {
    if (!isProfilePhoneRevealSaving) {
      setIsPhoneRevealModalOpen(false);
    }
  }, [isProfilePhoneRevealSaving]);

  const changePhoneRevealForm = React.useCallback((field, value) => {
    setPhoneRevealForm((current) => ({ ...current, [field]: value }));
  }, []);

  const submitProfileDetails = React.useCallback(async (event) => {
    event.preventDefault();
    await saveProfileDetails();
  }, [saveProfileDetails]);

  const submitEmailChange = React.useCallback(async (event) => {
    event.preventDefault();
    const saved = await saveProfileEmail(emailForm);
    if (saved) {
      setIsEmailModalOpen(false);
    }
  }, [emailForm, saveProfileEmail]);

  const submitEmailReveal = React.useCallback(async (event) => {
    event.preventDefault();
    const revealed = await revealProfileEmail(emailRevealForm);
    if (revealed) {
      setIsEmailRevealModalOpen(false);
    }
  }, [emailRevealForm, revealProfileEmail]);

  const submitPhoneReveal = React.useCallback(async (event) => {
    event.preventDefault();
    const revealed = await revealProfilePhone(phoneRevealForm);
    if (revealed) {
      setIsPhoneRevealModalOpen(false);
    }
  }, [phoneRevealForm, revealProfilePhone]);

  React.useEffect(() => {
    setDocumentsState(documents);
  }, [documents]);

  React.useEffect(() => {
    setVulnerabilityForm({
      flag: profile?.vulnerabilityFlag === true,
      notes: String(profile?.vulnerabilityNotes || ''),
    });
  }, [profile?.vulnerabilityFlag, profile?.vulnerabilityNotes]);

  const selectedDocument = React.useMemo(() => {
    if (!selectedDocumentId) {
      return null;
    }

    return documentsState.find(
      (item) => String(pickFirst(item?.uid, item?.id)) === selectedDocumentId,
    ) || null;
  }, [documentsState, selectedDocumentId]);

  React.useEffect(() => {
    if (!selectedDocument) {
      setDocumentForm({
        translation: '',
        expirationDate: '',
        status: '',
        translationRequired: false,
      });
      setDocumentSaveError('');
      setDocumentSaveSuccess('');
      return;
    }

    setDocumentForm({
      translation: String(pickFirst(selectedDocument.translation) || ''),
      expirationDate: formatDateInput(pickFirst(selectedDocument.expirationDate, selectedDocument.expiration_date)),
      status: String(pickFirst(selectedDocument.status) || '').trim(),
      translationRequired: isTrueLike(pickFirst(selectedDocument.translationRequired, selectedDocument.translation_required)),
    });
    setDocumentSaveError('');
    setDocumentSaveSuccess('');
    setDocumentDownloadError('');
  }, [selectedDocument]);

  React.useEffect(() => {
    if (!['documents', 'verification'].includes(activeTab) && selectedDocumentId) {
      setSelectedDocumentId('');
    }
  }, [activeTab, selectedDocumentId]);

  const closeDocumentSecurityStepUp = React.useCallback(() => {
    documentSecurityStepUpRef.current = null;
    setDocumentSecurityStepUp({
      isOpen: false,
      isSubmitting: false,
      error: '',
    });
  }, []);

  const submitDocumentSecurityStepUp = React.useCallback(async (code) => {
    const action = documentSecurityStepUpRef.current;
    const normalizedCode = String(code || '').trim();
    if (!action) {
      return false;
    }
    if (!normalizedCode) {
      setDocumentSecurityStepUp((prev) => ({
        ...prev,
        error: t('securityStepUp.codeRequired'),
      }));
      return false;
    }

    setDocumentSecurityStepUp((prev) => ({
      ...prev,
      isSubmitting: true,
      error: '',
    }));

    try {
      await adminApi.verifySecurityStepUp(token, { code: normalizedCode }, {
        requestId: action.requestId,
      });
      await action.retry();
      closeDocumentSecurityStepUp();
      return true;
    } catch (error) {
      setDocumentSecurityStepUp((prev) => ({
        ...prev,
        isSubmitting: false,
        error: isInvalidSecurityStepUpCodeError(error)
          ? t('securityStepUp.invalidCode')
          : error.message || t('securityStepUp.verifyFailed'),
      }));
      return false;
    }
  }, [closeDocumentSecurityStepUp, t, token]);

  React.useEffect(() => {
    let isCancelled = false;
    let objectUrl = '';

    if (!selectedDocument || !selectedDocumentId) {
      setDocumentViewer({
        isLoading: false,
        error: '',
        url: '',
        mime: '',
      });
      return undefined;
    }

    setDocumentViewer({
      isLoading: true,
      error: '',
      url: '',
      mime: '',
    });

    const loadDocument = async (requestId = createSecurityRequestId()) => {
      const normalizedId = encodeURIComponent(selectedDocumentId);
      const endpoints = [`/admin/document/client/download/${normalizedId}`];

      let lastError = '';

      for (const path of endpoints) {
        try {
          const response = await adminApi.requestBlobWithAuth({
            token,
            method: 'GET',
            path,
            headers: {
              'X-Request-Id': requestId,
            },
          });
          const blob = response.blob;
          objectUrl = URL.createObjectURL(blob);

          if (!isCancelled) {
            setDocumentViewer({
              isLoading: false,
              error: '',
              url: objectUrl,
              mime: blob.type || '',
            });
          }
          return;
        } catch (error) {
          if (isSecurityStepUpRequiredError(error)) {
            if (!isCancelled) {
              documentSecurityStepUpRef.current = {
                requestId,
                retry: () => loadDocument(requestId),
              };
              setDocumentViewer({
                isLoading: false,
                error: '',
                url: '',
                mime: '',
              });
              setDocumentSecurityStepUp({
                isOpen: true,
                isSubmitting: false,
                error: '',
              });
            }
            return;
          }
          if (isSensitiveActionRestrictedError(error)) {
            lastError = error.message || 'Sensitive action restricted';
            break;
          }
          lastError = error.message || 'Failed to load document';
        }
      }

      if (!isCancelled) {
        setDocumentViewer({
          isLoading: false,
          error: lastError || 'Failed to load document',
          url: '',
          mime: '',
        });
      }
    };

    loadDocument();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedDocument, selectedDocumentId, token]);

  const downloadSelectedDocument = React.useCallback(async (requestId = createSecurityRequestId()) => {
    if (!selectedDocumentId) {
      return;
    }

    const normalizedId = encodeURIComponent(selectedDocumentId);
    const fallbackFilename = String(pickFirst(
      selectedDocument?.name,
      selectedDocument?.type,
      selectedDocumentId,
      'document',
    ) || 'document').trim();

    setIsDocumentDownloading(true);
    setDocumentDownloadError('');

    try {
      const response = await adminApi.requestBlobWithAuth({
        token,
        method: 'GET',
        path: `/admin/document/client/download/${normalizedId}`,
        headers: {
          'X-Request-Id': requestId,
        },
        fallbackFilename,
      });
      triggerBlobDownload(response, fallbackFilename);
    } catch (error) {
      if (isSecurityStepUpRequiredError(error)) {
        documentSecurityStepUpRef.current = {
          requestId,
          retry: () => downloadSelectedDocument(requestId),
        };
        setDocumentSecurityStepUp({
          isOpen: true,
          isSubmitting: false,
          error: '',
        });
        return;
      }

      setDocumentDownloadError(error.message || 'Failed to download document');
    } finally {
      setIsDocumentDownloading(false);
    }
  }, [selectedDocument?.name, selectedDocument?.type, selectedDocumentId, token]);

  const saveSelectedDocument = React.useCallback(async () => {
    if (!selectedDocumentId) {
      return;
    }

    setIsDocumentSaving(true);
    setDocumentSaveError('');
    setDocumentSaveSuccess('');

    try {
      const payload = {
        translation: String(documentForm.translation || '').trim() || null,
        expirationDate: documentForm.expirationDate
          ? new Date(`${documentForm.expirationDate}T00:00:00.000Z`).toISOString()
          : null,
        status: String(documentForm.status || '').trim() || null,
        translationRequired: documentForm.translationRequired ? true : null,
      };

      const response = await adminApi.requestWithAuth({
        token,
        method: 'PATCH',
        path: `/admin/document/client/${encodeURIComponent(selectedDocumentId)}`,
        body: payload,
      });

      const refreshedDocuments = typeof refreshProfileDocuments === 'function'
        ? await refreshProfileDocuments({ silent: true })
        : null;

      if (Array.isArray(refreshedDocuments)) {
        setDocumentsState(refreshedDocuments);
      } else {
        const updatedDocuments = normalizeList(response?.documents);
        const updatedById = updatedDocuments.find(
          (item) => String(pickFirst(item?.uid, item?.id)) === selectedDocumentId,
        );

        setDocumentsState((prev) => prev.map((item) => {
          const itemId = String(pickFirst(item?.uid, item?.id));
          if (itemId !== selectedDocumentId) {
            return item;
          }

          return {
            ...item,
            ...(updatedById || {}),
            translation: payload.translation,
            expirationDate: payload.expirationDate,
            status: payload.status,
            translationRequired: payload.translationRequired,
          };
        }));
      }

      setDocumentSaveSuccess(t('documents.documentUpdated'));
      setSelectedDocumentId('');
    } catch (error) {
      setDocumentSaveError(error.message || 'Failed to update document');
    } finally {
      setIsDocumentSaving(false);
    }
  }, [
    documentForm.expirationDate,
    documentForm.status,
    documentForm.translation,
    documentForm.translationRequired,
    refreshProfileDocuments,
    selectedDocumentId,
    t,
    token,
  ]);

  const handleDocumentClose = React.useCallback(() => {
    setSelectedDocumentId('');
  }, []);

  const saveVulnerability = React.useCallback(async (event) => {
    event.preventDefault();

    try {
      await updateVulnerability({
        flag: vulnerabilityForm.flag,
        notes: String(vulnerabilityForm.notes || '').trim() || null,
      });
    } catch {
      // Error state is surfaced through shared floating alerts.
    }
  }, [updateVulnerability, vulnerabilityForm.flag, vulnerabilityForm.notes]);

  const submitSuitability = React.useCallback(async (event) => {
    event.preventDefault();
    setSuitabilityFormError('');

    let answers = null;
    const rawAnswers = String(suitabilityForm.answersText || '').trim();
    if (rawAnswers) {
      try {
        answers = JSON.parse(rawAnswers);
      } catch {
        setSuitabilityFormError(t('profiles.answersInvalidJson'));
        return;
      }
    }

    try {
      await createSuitabilityAssessment({
        result: suitabilityForm.result || null,
        override: suitabilityForm.override === true,
        notes: String(suitabilityForm.notes || '').trim() || null,
        answers,
      });
      setSuitabilityForm({
        result: 'suitable',
        override: false,
        notes: '',
        answersText: '',
      });
    } catch {
      // Error state is surfaced through shared floating alerts.
    }
  }, [createSuitabilityAssessment, suitabilityForm.answersText, suitabilityForm.notes, suitabilityForm.override, suitabilityForm.result, t]);

  const submitProfileTransactionFilters = React.useCallback((event) => {
    event.preventDefault();
    if (typeof applyProfileTransactionFilters === 'function') {
      applyProfileTransactionFilters();
    }
  }, [applyProfileTransactionFilters]);

  const renderProfileTransactionSortIcon = React.useCallback((field) => {
    if (profileTransactionSort.sortBy !== field) {
      return <i className="ti ti-arrows-sort ms-1" aria-hidden="true" />;
    }

    return profileTransactionSort.sortOrder === 'asc'
      ? <i className="ti ti-sort-ascending ms-1" aria-hidden="true" />
      : <i className="ti ti-sort-descending ms-1" aria-hidden="true" />;
  }, [profileTransactionSort.sortBy, profileTransactionSort.sortOrder]);

  const tabError = activeTab === 'kyc'
    ? (kycError || kycSettingsError)
    : activeTab === 'accounts'
      ? (accountsError || accountSyncError)
      : activeTab === 'transactions'
        ? profileTransactionsError
      : activeTab === 'partnership'
        ? partnershipError
      : activeTab === 'documents'
        ? documentsError
        : activeTab === 'actionLog'
          ? actionLogsError
        : activeTab === 'verification'
          ? verifyProfileError
        : '';
  const tabSuccess = activeTab === 'accounts'
    ? accountSyncSuccess
    : activeTab === 'verification'
      ? verifyProfileSuccess
      : '';
  const topError = !isLoading ? error : '';
  const complianceInfo = rawProfile?.legalHoldUntil
    ? `Legal hold active until ${formatDate(rawProfile.legalHoldUntil)}`
    : '';
  const profileHeaderName = profile?.name || '-';
  const profileHeaderEmail = profile?.email || '-';
  const profileHeaderSubtitle = rawProfile ? (
    <span className="app-profile-details-header-meta">
      <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(profile?.status)}`}>
        {profile?.status || '-'}
      </span>
      {profile?.legalHoldUntil ? (
        <span className="badge rounded-pill fw-semibold text-uppercase bg-warning-subtle text-warning">
          legal hold
        </span>
      ) : null}
      {isFinancialRegulation && profile?.vulnerabilityFlag === true ? (
        <span className="badge rounded-pill fw-semibold text-uppercase bg-danger-subtle text-danger">
          vulnerable
        </span>
      ) : null}
      <span>{profileHeaderName}</span>
      <span className="text-muted">{`<${profileHeaderEmail}>`}</span>
    </span>
  ) : (profileId ? `${t('profiles.profile')} #${profileId}` : t('profiles.profile'));

  return (
    <div>
      <FloatingAlerts
        error={topError || profileSaveError || complianceError || tabError}
        success={profileSaveSuccess || complianceSuccess || tabSuccess}
        info={complianceInfo}
      />
      <PageHeader title={t('profiles.detailsTitle')} subtitle={profileHeaderSubtitle} />

      <div className="widget-content searchable-container list">
        {isLoading ? <div className="text-muted mb-2">{t('profiles.loadingProfile')}</div> : null}

        {!isLoading && !error && rawProfile ? (
          <>
            <div className="app-profile-details-tabs">
              <ul className="nav nav-tabs flex-nowrap">
                {[...tabs, ...(calls.canView ? [{ key: 'calls', label: 'calls.title' }] : [])].map((tab) => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {t(tab.label)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {activeTab === 'kyc' ? (
              <div className="tab-content mt-3">
                <div className="tab-pane fade active show">
                  {isKycLoading ? <div className="text-muted">{t('profiles.loadingKyc')}</div> : null}
                  {!isKycLoading && !kycError ? (
                    <>
                      {isKycSettingsLoading ? <div className="text-muted small mb-2">{t('profiles.loadingKycSections')}</div> : null}
                      {!kycStepRows.length ? (
                        <div className="text-muted">{t('profiles.noKycData')}</div>
                      ) : (
                        <div className="d-flex flex-column gap-3">
                          {kycStepRows.map((step, stepIndex) => (
                            <div key={step.id} className="card card-body">
                              <h5 className="mb-3">{step.name || `Step ${stepIndex + 1}`}</h5>
                              <div className="row g-3">
                                {step.fields.length === 0 ? (
                                  <div className="col-12 text-muted">{t('profiles.noFieldsInSection')}</div>
                                ) : null}
                                {step.fields.map((field) => (
                                  <div key={`${step.id}-${field.id}`} className="col-md-6">
                                    <label className="form-label">{field.name}</label>
                                    {String(field.value).includes('\n') || String(field.value).length > 120 ? (
                                      <textarea className="form-control" value={field.value} rows={4} disabled />
                                    ) : (
                                      <input type="text" className="form-control" value={field.value} disabled />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className={`${activeTab === 'verification' ? '' : 'card card-body rounded-top-0'} mt-3`}>
                <div className="tab-content">
                  {activeTab === 'calls' && calls.canView && <CallsPage key={profileId} profileId={profileId} profileLabel={profile?.name || ''} />}
                  <div className={`tab-pane fade ${activeTab === 'profile' ? 'active show' : ''}`}>
                    <div className="d-flex flex-column gap-3">
                      <ProfileIdentityContactTab
                        profileId={profileId}
                        profile={profile}
                        canEdit={canUpdateProfile}
                        form={profileEditForm}
                        errors={profileEditErrors}
                        isSaving={isProfileSaving}
                        onFieldChange={updateProfileEditField}
                        onSave={submitProfileDetails}
                        onCancel={resetProfileEditForm}
                        onOpenEmailModal={openEmailModal}
                        canRevealEmail={canRevealProfilePii}
                        revealedEmail={revealedEmail}
                        onOpenEmailRevealModal={openEmailRevealModal}
                        revealedPhone={revealedPhone}
                        onOpenPhoneRevealModal={openPhoneRevealModal}
                      />
                    </div>
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'compliance' ? 'active show' : ''}`}>
                    <div className="d-flex flex-column gap-3">
                      <div className="card card-body">
                        <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3">
                          <div className="row g-3 small flex-grow-1">
                            <div className="col-sm-6 col-xl-3">
                              <div className="text-muted text-uppercase mb-1">{t('profiles.marketingConsent')}</div>
                              <div>{formatBooleanLabel(profile?.marketingConsent)}</div>
                            </div>
                            <div className="col-sm-6 col-xl-3">
                              <div className="text-muted text-uppercase mb-1">{t('profiles.marketingConsentAt')}</div>
                              <div>{formatDate(profile?.marketingConsentAt)}</div>
                            </div>
                            <div className="col-sm-6 col-xl-3">
                              <div className="text-muted text-uppercase mb-1">{t('profiles.closureRequested')}</div>
                              <div>{formatDate(profile?.closureRequestedAt)}</div>
                            </div>
                            <div className="col-sm-6 col-xl-3">
                              <div className="text-muted text-uppercase mb-1">{t('profiles.consentWithdrawn')}</div>
                              <div>{formatDate(profile?.consentWithdrawnAt)}</div>
                            </div>
                            <div className="col-sm-6 col-xl-3">
                              <div className="text-muted text-uppercase mb-1">{t('profiles.legalHoldUntil')}</div>
                              <div>{formatDate(profile?.legalHoldUntil)}</div>
                            </div>
                          </div>

                          <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={exportProfileData}
                              disabled={isExportingProfile || isAnonymizingProfile}
                            >
                              {isExportingProfile ? t('profiles.exporting') : t('profiles.exportData')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={anonymizeProfile}
                              disabled={isAnonymizingProfile || isExportingProfile || String(profile?.status || '').trim().toLowerCase() === 'closed'}
                            >
                              {isAnonymizingProfile ? t('profiles.anonymizing') : t('profiles.anonymizeProfile')}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="card card-body">
                        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                          <div>
                            <h5 className="mb-1">{t('profiles.acceptedLegalContent')}</h5>
                            <p className="text-muted mb-0">{t('profiles.acceptedLegalContentSubtitle')}</p>
                          </div>
                        </div>

                        {legalAcceptanceRows.length === 0 ? (
                          <div className="text-muted">{t('profiles.noLegalAcceptances')}</div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>{t('common.type')}</th>
                                  <th>{t('profiles.titleOrName')}</th>
                                  <th>{t('profiles.version')}</th>
                                  <th>{t('profiles.effectiveDate')}</th>
                                  <th>{t('profiles.acceptedAt')}</th>
                                  <th>{t('profiles.consentIp')}</th>
                                  <th>{t('profiles.currentStatus')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {legalAcceptanceRows.map((acceptance) => (
                                  <tr key={acceptance.key}>
                                    <td>{acceptance.type}</td>
                                    <td className="text-break">{acceptance.title}</td>
                                    <td>{acceptance.version}</td>
                                    <td>{formatDate(acceptance.effectiveDate)}</td>
                                    <td>{formatDate(acceptance.acceptedAt)}</td>
                                    <td>{acceptance.consentIp}</td>
                                    <td>
                                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(acceptance.status)}`}>
                                        {acceptance.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {isFinancialRegulation ? (
                        <div className="row g-3">
                          <div className="col-12 col-xxl-6">
                            <div className="card card-body h-100">
                              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                                <div>
                                  <h5 className="mb-1">{t('profiles.vulnerabilityFlag')}</h5>
                                  <p className="text-muted mb-0">{t('profiles.vulnerabilityFlagSubtitle')}</p>
                                </div>
                                <span className={`badge rounded-pill fw-semibold text-uppercase ${profile?.vulnerabilityFlag === true ? 'bg-danger-subtle text-danger' : 'app-badge-disabled'}`}>
                                  {profile?.vulnerabilityFlag === true ? 'flagged' : 'clear'}
                                </span>
                              </div>

                              <form onSubmit={saveVulnerability}>
                                <div className="form-check form-switch mb-3">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="profile-vulnerability-flag"
                                    checked={vulnerabilityForm.flag}
                                    onChange={(event) => setVulnerabilityForm((prev) => ({ ...prev, flag: event.target.checked }))}
                                    disabled={isUpdatingVulnerability}
                                  />
                                  <label className="form-check-label" htmlFor="profile-vulnerability-flag">
                                    {t('profiles.markVulnerable')}
                                  </label>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">{t('profiles.notes')}</label>
                                  <textarea
                                    className="form-control"
                                    rows={4}
                                    value={vulnerabilityForm.notes}
                                    onChange={(event) => setVulnerabilityForm((prev) => ({ ...prev, notes: event.target.value }))}
                                    disabled={isUpdatingVulnerability}
                                    placeholder={t('profiles.vulnerabilityNotesPlaceholder')}
                                  />
                                </div>
                                <div className="d-flex justify-content-end">
                                  <button type="submit" className="btn btn-outline-primary" disabled={isUpdatingVulnerability}>
                                    {isUpdatingVulnerability ? t('metrics.saving') : t('profiles.saveVulnerability')}
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>

                          <div className="col-12 col-xxl-6">
                            <div className="card card-body h-100">
                              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                                <div>
                                  <h5 className="mb-1">{t('profiles.auditIntegrity')}</h5>
                                  <p className="text-muted mb-0">{t('profiles.auditIntegritySubtitle')}</p>
                                </div>
                                <span className={`badge rounded-pill fw-semibold text-uppercase ${getAuditBadgeClass(auditIntegrity?.isValid)}`}>
                                  {auditIntegrity ? (auditIntegrity.isValid ? t('profiles.auditValid') : t('profiles.auditMismatch')) : t('profiles.auditNotChecked')}
                                </span>
                              </div>

                              {auditIntegrity ? (
                                <div className="row g-3 small mb-3">
                                  <div className="col-sm-4">
                                    <div className="text-muted text-uppercase mb-1">{t('profiles.entriesChecked')}</div>
                                    <div>{auditIntegrity.checkedEntries ?? 0}</div>
                                  </div>
                                  <div className="col-sm-4">
                                    <div className="text-muted text-uppercase mb-1">{t('profiles.mismatches')}</div>
                                    <div>{auditIntegrity.mismatchCount ?? 0}</div>
                                  </div>
                                  <div className="col-sm-4">
                                    <div className="text-muted text-uppercase mb-1">{t('profiles.profileUid')}</div>
                                    <div className="text-break">{auditIntegrity.profileUid || profileId || '-'}</div>
                                  </div>
                                  {Array.isArray(auditIntegrity.mismatchEntryIds) && auditIntegrity.mismatchEntryIds.length ? (
                                    <div className="col-12">
                                      <div className="text-muted text-uppercase mb-1">{t('profiles.mismatchEntryIds')}</div>
                                      <div className="text-break">{auditIntegrity.mismatchEntryIds.join(', ')}</div>
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="text-muted small mb-3">{t('profiles.auditIntegrityHint')}</div>
                              )}

                              <div className="d-flex justify-content-end">
                                <button type="button" className="btn btn-outline-primary" onClick={checkAuditIntegrity} disabled={isAuditChecking}>
                                  {isAuditChecking ? t('profiles.checking') : t('profiles.runAuditCheck')}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="card card-body">
                              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                                <div>
                                  <h5 className="mb-1">{t('profiles.suitabilityAssessments')}</h5>
                                  <p className="text-muted mb-0">{t('profiles.suitabilityAssessmentsSubtitle')}</p>
                                </div>
                                <span className="badge rounded-pill fw-semibold text-uppercase bg-info-subtle text-info">
                                  {suitabilityAssessments.length} total
                                </span>
                              </div>

                              <div className="row g-4">
                                <div className="col-lg-5">
                                  <form onSubmit={submitSuitability}>
                                    <div className="row g-3">
                                      <div className="col-sm-6">
                                        <label className="form-label">{t('profiles.result')}</label>
                                        <select
                                          className="form-select"
                                          value={suitabilityForm.result}
                                          onChange={(event) => setSuitabilityForm((prev) => ({ ...prev, result: event.target.value }))}
                                          disabled={isCreatingSuitability}
                                        >
                                          <option value="suitable">{t('profiles.suitable')}</option>
                                          <option value="not_suitable">{t('profiles.notSuitable')}</option>
                                          <option value="requires_acknowledgement">{t('profiles.requiresAcknowledgement')}</option>
                                        </select>
                                      </div>
                                      <div className="col-sm-6 d-flex align-items-end">
                                        <div className="form-check form-switch mb-2">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="profile-suitability-override"
                                            checked={suitabilityForm.override}
                                            onChange={(event) => setSuitabilityForm((prev) => ({ ...prev, override: event.target.checked }))}
                                            disabled={isCreatingSuitability}
                                          />
                                          <label className="form-check-label" htmlFor="profile-suitability-override">
                                            {t('profiles.manualOverride')}
                                          </label>
                                        </div>
                                      </div>
                                      <div className="col-12">
                                        <label className="form-label">{t('profiles.notes')}</label>
                                        <textarea
                                          className="form-control"
                                          rows={3}
                                          value={suitabilityForm.notes}
                                          onChange={(event) => setSuitabilityForm((prev) => ({ ...prev, notes: event.target.value }))}
                                          disabled={isCreatingSuitability}
                                          placeholder={t('profiles.suitabilityNotesPlaceholder')}
                                        />
                                      </div>
                                      <div className="col-12">
                                        <label className="form-label">{t('profiles.answersJson')}</label>
                                        <textarea
                                          className={`form-control ${suitabilityFormError ? 'is-invalid' : ''}`}
                                          rows={5}
                                          value={suitabilityForm.answersText}
                                          onChange={(event) => setSuitabilityForm((prev) => ({ ...prev, answersText: event.target.value }))}
                                          disabled={isCreatingSuitability}
                                          placeholder={'{"experience":"advanced","riskTolerance":"medium"}'}
                                        />
                                        {suitabilityFormError ? <div className="invalid-feedback">{suitabilityFormError}</div> : null}
                                      </div>
                                      <div className="col-12 d-flex justify-content-end">
                                        <button type="submit" className="btn btn-primary" disabled={isCreatingSuitability}>
                                          {isCreatingSuitability ? t('metrics.saving') : t('profiles.addAssessment')}
                                        </button>
                                      </div>
                                    </div>
                                  </form>
                                </div>

                                <div className="col-lg-7">
                                  {isSuitabilityLoading ? <div className="text-muted">{t('profiles.loadingSuitability')}</div> : null}
                                  {!isSuitabilityLoading && suitabilityError ? (
                                    <div className="alert alert-danger mb-0">{suitabilityError}</div>
                                  ) : null}
                                  {!isSuitabilityLoading && !suitabilityError ? (
                                    suitabilityAssessments.length === 0 ? (
                                      <div className="text-muted">{t('profiles.noSuitabilityAssessments')}</div>
                                    ) : (
                                      <div className="table-responsive">
                                        <table className="table align-middle mb-0">
                                          <thead>
                                            <tr>
                                              <th>{t('common.status')}</th>
                                              <th>{t('profiles.result')}</th>
                                              <th>{t('common.date')}</th>
                                              <th>{t('profiles.by')}</th>
                                              <th>{t('profiles.notes')}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {suitabilityAssessments.map((assessment, index) => (
                                              <tr key={assessment.uid || index}>
                                                <td>
                                                  <span className={`badge rounded-pill fw-semibold text-uppercase ${getSuitabilityBadgeClass(assessment.status, assessment.result)}`}>
                                                    {assessment.status || '-'}
                                                  </span>
                                                </td>
                                                <td>{assessment.result || '-'}</td>
                                                <td>{formatDate(assessment.created)}</td>
                                                <td>{assessment.createdByEmail || '-'}</td>
                                                <td className="text-break">
                                                  {assessment.notes || (
                                                    assessment.answers
                                                      ? JSON.stringify(assessment.answers)
                                                      : '-'
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'accounts' ? 'active show' : ''}`}>
                    {isAccountsLoading ? <div className="text-muted">{t('profiles.loadingAccounts')}</div> : null}
                    {!isAccountsLoading && !accountsError ? (
                      !accounts.length ? (
                        <div className="text-muted">{t('profiles.noAccounts')}</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table search-table align-middle text-nowrap mb-0">
                            <thead className="header-item">
                              <tr>
                                <th>{t('profiles.login')}</th>
                                <th>{t('common.type')}</th>
                                <th>{t('profiles.platform')}</th>
                                <th>{t('profiles.currency')}</th>
                                <th>{t('profiles.balance')}</th>
                                <th>{t('profiles.equity')}</th>
                                <th>{t('profiles.freeMargin')}</th>
                                <th>{t('profiles.usedMargin')}</th>
                                <th>{t('profiles.leverage')}</th>
                                <th>{t('profiles.created')}</th>
                                <th>{t('common.actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accounts.map((account, index) => {
                                const sync = extractAccountSync(account);
                                const accountId = String(pickFirst(account.id, account.uid) || '').trim();
                                const accountType = pickFirst(account.type) || '-';
                                const isPendingVerification = isPendingVerificationAccount(account);
                                const isSyncDisabled = Boolean(syncingAccountId) || isPendingVerification;
                                const loginContent = isPendingVerification ? (
                                  <PendingVerificationBadge label={t('accounts.pendingVerification')} />
                                ) : (
                                  pickFirst(account.login) || '-'
                                );

                                return (
                                  <tr key={pickFirst(account.uid, account.id, `${account.login}-${index}`)}>
                                    <td>
                                      {accountId ? (
                                        <EntityLink
                                          to={`/accounts/${accountId}`}
                                          state={{ from: location.pathname + location.search }}
                                        >
                                          {loginContent}
                                        </EntityLink>
                                      ) : (
                                        loginContent
                                      )}
                                    </td>
                                    <td>
                                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getAccountTypeBadgeClass(accountType)}`}>
                                        {accountType}
                                      </span>
                                    </td>
                                    <td>{pickFirst(account.platform) || '-'}</td>
                                    <td>{pickFirst(account.currency) || '-'}</td>
                                    <td>{formatMoney(getAccountMoney(account, 'balance'), locale, { fallback: '-', trimTrailingZeros: true })}</td>
                                    <td>{formatMoney(getAccountMoney(account, 'equity'), locale, { fallback: '-', trimTrailingZeros: true })}</td>
                                    <td>{formatMoney(getAccountMoney(account, 'freeMargin'), locale, { fallback: '-', trimTrailingZeros: true })}</td>
                                    <td>{formatMoney(getAccountMoney(account, 'usedMargin'), locale, { fallback: '-', trimTrailingZeros: true })}</td>
                                    <td>{pickFirst(account.leverage, pickSyncField(sync, 'leverage')) || '-'}</td>
                                    <td>{formatDate(pickFirst(account.created, account.createdAt, account.created_at))}</td>
                                    <td>
                                      <div className="btn-group btn-group-sm" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary"
                                          onClick={() => syncAccount(account)}
                                          disabled={isSyncDisabled}
                                          title={isPendingVerification ? t('accounts.pendingVerificationSyncDisabled') : undefined}
                                        >
                                          {syncingAccountId && syncingAccountId === accountId ? t('profiles.syncing') : t('profiles.sync')}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'transactions' ? 'active show' : ''}`}>
                    <form className="card card-body mb-3" onSubmit={submitProfileTransactionFilters}>
                      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                        <h5 className="mb-0">{t('profiles.transactionFilters')}</h5>
                        <div className="d-flex gap-2">
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            data-testid="profile-transactions-apply"
                          >
                            {t('profiles.applyTransactionFilters')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={clearProfileTransactionFilters}
                          >
                            {t('profiles.clearTransactionFilters')}
                          </button>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-4 col-xl-3">
                          <label className="form-label" htmlFor="profile-transaction-type">
                            {t('profiles.transactionType')}
                          </label>
                          <select
                            id="profile-transaction-type"
                            name="type"
                            className="form-select"
                            value={profileTransactionFilters.type || ''}
                            onChange={updateProfileTransactionFilter}
                          >
                            <option value="">{t('profiles.transactionAllTypes')}</option>
                            <option value="deposit">deposit</option>
                            <option value="withdrawal">withdrawal</option>
                          </select>
                        </div>
                        <div className="col-md-4 col-xl-3">
                          <label className="form-label" htmlFor="profile-transaction-method">
                            {t('profiles.transactionMethod')}
                          </label>
                          <input
                            id="profile-transaction-method"
                            name="method"
                            type="text"
                            className="form-control"
                            value={profileTransactionFilters.method || ''}
                            onChange={updateProfileTransactionFilter}
                          />
                        </div>
                        <div className="col-md-4 col-xl-3">
                          <label className="form-label" htmlFor="profile-transaction-status">
                            {t('profiles.transactionStatus')}
                          </label>
                          <select
                            id="profile-transaction-status"
                            name="status"
                            className="form-select"
                            value={profileTransactionFilters.status || ''}
                            onChange={updateProfileTransactionFilter}
                          >
                            <option value="">{t('profiles.transactionAllStatuses')}</option>
                            {profileTransactionStatusOptions.map((status) => (
                              <option value={status} key={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </form>

                    {isProfileTransactionsLoading ? (
                      <div className="text-muted">{t('profiles.loadingTransactions')}</div>
                    ) : null}
                    {!isProfileTransactionsLoading && !profileTransactionsError ? (
                      !profileTransactionsRows.length ? (
                        <div className="text-muted">{t('profiles.noTransactions')}</div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <table className="table search-table align-middle text-nowrap mb-0">
                              <thead className="header-item">
                                <tr>
                                  <th>{t('profiles.transactionReference')}</th>
                                  <th>{t('common.type')}</th>
                                  <th>{t('common.status')}</th>
                                  <th>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-decoration-none fw-semibold"
                                      data-testid="profile-transactions-sort-amount"
                                      onClick={() => changeProfileTransactionSort('amount')}
                                    >
                                      {t('common.amount')}
                                      {renderProfileTransactionSortIcon('amount')}
                                    </button>
                                  </th>
                                  <th>{t('profiles.transactionMethod')}</th>
                                  <th>{t('profiles.transactionAccount')}</th>
                                  <th>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-decoration-none fw-semibold"
                                      data-testid="profile-transactions-sort-created"
                                      onClick={() => changeProfileTransactionSort('created')}
                                    >
                                      {t('common.date')}
                                      {renderProfileTransactionSortIcon('created')}
                                    </button>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {profileTransactionsRows.map((transaction) => (
                                  <tr key={transaction.key || transaction.id || transaction.reference}>
                                    <td>
                                      {transaction.id ? (
                                        <EntityLink
                                          to={`/transactions/${transaction.id}`}
                                          state={{ from: location.pathname + location.search }}
                                        >
                                          {transaction.reference || transaction.id}
                                        </EntityLink>
                                      ) : (
                                        transaction.reference || '-'
                                      )}
                                    </td>
                                    <td>
                                      <span className="badge rounded-pill fw-semibold text-uppercase app-badge-disabled">
                                        {transaction.type || '-'}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getTransactionStatusBadgeClass(transaction.status)}`}>
                                        {transaction.status || '-'}
                                      </span>
                                    </td>
                                    <td>{transaction.amountFormatted || '-'}</td>
                                    <td>{transaction.methodId || '-'}</td>
                                    <td>{transaction.accountLogin || '-'}</td>
                                    <td>{transaction.createdAt || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {hasMoreProfileTransactions ? (
                            <div className="d-flex justify-content-center mt-3">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={loadMoreProfileTransactions}
                                disabled={isProfileTransactionsLoadingMore}
                              >
                                {isProfileTransactionsLoadingMore
                                  ? t('profiles.loadingTransactions')
                                  : t('profiles.loadMoreTransactions')}
                              </button>
                            </div>
                          ) : null}
                        </>
                      )
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'partnership' ? 'active show' : ''}`}>
                    {isPartnershipLoading ? <div className="text-muted">{t('profiles.loadingPartnership')}</div> : null}
                    {!isPartnershipLoading && partnershipError ? (
                      <div className="alert alert-danger mb-0">{partnershipError}</div>
                    ) : null}
                    {!isPartnershipLoading && !partnershipError ? (
                      <div className="d-flex flex-column gap-3">
                        <div className="card card-body">
                          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                            <div>
                              <h5 className="mb-1">{t('profiles.historyPartners')}</h5>
                              <p className="text-muted mb-0">{t('profiles.historyPartnersSubtitle')}</p>
                            </div>
                          </div>

                          {!partnershipData.historyPartners.length ? (
                            <div className="text-muted">{t('profiles.noPartnershipHistory')}</div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th>{t('common.name')}</th>
                                    <th>{t('common.email')}</th>
                                    <th>{t('profiles.lastSeenAt')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {partnershipData.historyPartners.map((partner, index) => (
                                    <tr key={partner.key || partner.uid || index}>
                                      <td>{partner.name || '-'}</td>
                                      <td>{partner.email || '-'}</td>
                                      <td>{formatDate(partner.lastSeenAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        <div className="card card-body">
                          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                            <div>
                              <h5 className="mb-1">{t('profiles.partnershipAccounts')}</h5>
                              <p className="text-muted mb-0">{t('profiles.partnershipAccountsSubtitle')}</p>
                            </div>
                          </div>

                          {!partnershipData.accounts.length ? (
                            <div className="text-muted">{t('profiles.noAccounts')}</div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table search-table align-middle text-nowrap mb-0">
                                <thead className="header-item">
                                  <tr>
                                    <th>{t('profiles.login')}</th>
                                    <th>{t('common.type')}</th>
                                    <th>{t('profiles.platform')}</th>
                                    <th>{t('profiles.currency')}</th>
                                    <th>{t('profiles.currentPartner')}</th>
                                    <th>{t('common.actions')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {partnershipData.accounts.map((account, index) => (
                                    <tr key={account.key || account.uid || `${account.login}-${index}`}>
                                      <td>{account.login || '-'}</td>
                                      <td>{account.type || '-'}</td>
                                      <td>{account.platform || '-'}</td>
                                      <td>{account.currency || '-'}</td>
                                      <td>
                                        {account.currentPartner ? (
                                          <div className="d-flex flex-column">
                                            <span className="fw-semibold">{formatPersonName(account.currentPartner)}</span>
                                            {account.currentPartner.email ? (
                                              <span className="text-muted small">{account.currentPartner.email}</span>
                                            ) : null}
                                          </div>
                                        ) : (
                                          <span className="badge rounded-pill fw-semibold text-uppercase app-badge-disabled">
                                            {t('profiles.unlinked')}
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => openPartnershipModal(account)}
                                          aria-label={t('profiles.changePartner')}
                                        >
                                          <i className="ti ti-edit me-1" aria-hidden="true" />
                                          {t('profiles.changePartner')}
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'trades' ? 'active show' : ''}`}>
                    <TradesTable
                      trades={trades}
                      total={tradesTotal}
                      totalLots={tradesTotalLots}
                      totalProfit={tradesTotalProfit}
                      profitCurrency={tradesProfitCurrency}
                      isLoading={isTradesLoading}
                      error={tradesError}
                      showAccountLogin={true}
                      symbolFilter={tradesSymbolFilter}
                      dateFromFilter={tradesDateFromFilter}
                      dateToFilter={tradesDateToFilter}
                      onSymbolChange={handleTradesSymbolChange}
                      onDateFromChange={handleTradesDateFromChange}
                      onDateToChange={handleTradesDateToChange}
                      onLoadMore={loadMoreTrades}
                      hasMore={trades.length < tradesTotal}
                    />
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'verification' ? 'active show' : ''}`}>
                    {activeTab === 'verification' && <VerificationAccessCard profileId={profileId} refreshKey={`${rawProfile?.status}|${rawProfile?.invalid}|${verifyProfileSuccess}`} />}
                    {isDocumentsLoading ? <div className="text-muted">{t('profiles.loadingVerification')}</div> : null}
                    {!isDocumentsLoading ? (
                      <ProfileVerificationTab
                        profile={rawProfile}
                        documents={documentsState}
                        kycData={kyc}
                        kycSettingsSteps={kycSettings}
                        token={token}
                        canViewExternalVerifications={canViewExternalVerifications}
                        onExternalVerificationChanged={reloadVerificationProfile}
                        onVerifyProfile={verifyProfile}
                        onRequestInformation={requestProfileInformation}
                        onInvalidateProfile={invalidateProfile}
                        onValidateProfile={validateProfile}
                        isVerifying={isVerifyingProfile}
                        isRequestingInformation={isRequestingInformation}
                        isInvalidatingProfile={isInvalidatingProfile}
                        isValidatingProfile={isValidatingProfile}
                        verifyError={verifyProfileError}
                        verifySuccess={verifyProfileSuccess}
                        showInlineAlerts={false}
                        onOpenDocument={(documentId) => setSelectedDocumentId(String(documentId || ''))}
                      />
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'documents' ? 'active show' : ''}`}>
                    {isDocumentsLoading ? <div className="text-muted">{t('profiles.loadingDocuments')}</div> : null}
                    {!isDocumentsLoading && !documentsError ? (
                      !documentsState.length ? (
                        <div className="text-muted">{t('profiles.noDocuments')}</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table search-table align-middle text-nowrap mb-0">
                            <thead className="header-item">
                              <tr>
                                <th>{t('common.name')}</th>
                                <th>{t('common.type')}</th>
                                <th>{t('common.status')}</th>
                                <th>{t('documents.translation')}</th>
                                <th>{t('documents.created')}</th>
                                <th>{t('documents.modified')}</th>
                                <th>{t('documents.expiration')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentsState.map((document, index) => (
                                <tr
                                  key={pickFirst(document.uid, document.id, `${document.name}-${index}`)}
                                  className="app-clickable-row"
                                  onClick={() => setSelectedDocumentId(String(pickFirst(document.uid, document.id) || ''))}
                                >
                                  <td>{pickFirst(document.name) || '-'}</td>
                                  <td>
                                    <span className={`badge rounded-pill fw-semibold text-uppercase ${getDocumentTypeBadgeClass(pickFirst(document.type))}`}>
                                      {pickFirst(document.type) || '-'}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge rounded-pill fw-semibold text-uppercase ${getDocumentStatusBadgeClass(pickFirst(document.status))}`}>
                                      {pickFirst(document.status) || '-'}
                                    </span>
                                  </td>
                                  <td>
                                    {(() => {
                                      const translationMeta = getTranslationBadgeMeta(document);
                                      return (
                                        <span className={`badge rounded-pill fw-semibold text-uppercase ${translationMeta.className}`}>
                                          {translationMeta.label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>{formatDate(pickFirst(document.created, document.createdAt, document.created_at))}</td>
                                  <td>{formatDate(pickFirst(document.modified, document.updatedAt, document.updated_at))}</td>
                                  <td>{formatDate(pickFirst(document.expirationDate, document.expiration_date))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'actionLog' ? 'active show' : ''}`}>
                    {isActionLogsLoading ? <div className="text-muted">{t('profiles.loadingActionLog')}</div> : null}
                    {!isActionLogsLoading && !actionLogsError ? (
                      !actionLogs.length ? (
                        <div className="text-muted">{t('profiles.noActionLogs')}</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table search-table align-middle mb-0">
                            <thead className="header-item">
                              <tr>
                                <th style={{ width: '180px' }}>{t('profiles.created')}</th>
                                <th style={{ width: '220px' }}>{t('profiles.action')}</th>
                                <th>{t('profiles.details')}</th>
                                <th style={{ width: '150px' }}>{t('profiles.ip')}</th>
                                <th style={{ width: '220px' }}>{t('profiles.owner')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {actionLogs.map((item, index) => (
                                <tr key={`${pickFirst(item.created, item.action, item.ip, index)}-${index}`}>
                                  <td>{formatDate(pickFirst(item.created, item.createdAt, item.created_at))}</td>
                                  <td>{pickFirst(item.action) || '-'}</td>
                                  <td className="text-break">
                                    <ActionLogDetails details={pickFirst(item.details)} />
                                  </td>
                                  <td>{pickFirst(item.ip) || '-'}</td>
                                  <td>{formatPersonName(item.owner)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : null}
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'internalMail' ? 'active show' : ''}`}>
                    <div className="d-flex justify-content-end mb-3">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreateMail}>
                        <i className="ti ti-mail-forward me-1"></i>
                        {t('profiles.newMessage')}
                      </button>
                    </div>
                    {isProfileMailLoading ? <div className="text-muted">{t('profiles.loadingMessages')}</div> : null}
                    {!isProfileMailLoading && profileMailError ? (
                      <div className="alert alert-danger">{profileMailError}</div>
                    ) : null}
                    {!isProfileMailLoading && !profileMailError ? (
                      !profileMail.length ? (
                        <div className="text-muted">{t('profiles.noMessages')}</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table search-table align-middle mb-0">
                            <thead className="header-item">
                              <tr>
                                <th>{t('profiles.subject')}</th>
                                <th>{t('common.type')}</th>
                                <th style={{ width: '180px' }}>{t('profiles.sentAt')}</th>
                                <th style={{ width: '220px' }}>{t('profiles.sentBy')}</th>
                                <th style={{ width: '100px' }}>{t('profiles.read')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {profileMail.map((msg, index) => (
                                <tr key={msg.uid || index}>
                                  <td className="fw-semibold">{msg.subject || '-'}</td>
                                  <td>
                                    <span className={`badge rounded-pill fw-semibold text-uppercase ${getMessageTypeBadgeClass(msg.type)}`}>
                                      {msg.type || 'transactional'}
                                    </span>
                                  </td>
                                  <td>{formatDate(msg.created)}</td>
                                  <td>{msg.sentByEmail || '-'}</td>
                                  <td>
                                    {msg.readCount > 0 ? (
                                      <span className="badge bg-success-subtle text-success">{t('profiles.readStatus')}</span>
                                    ) : (
                                      <span className="badge bg-secondary-subtle text-secondary">{t('profiles.unreadStatus')}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      <ProfilePartnershipModal
        isOpen={isPartnershipModalOpen}
        account={partnershipModalAccount}
        searchQuery={partnershipPartnerQuery}
        selectedPartner={selectedPartnershipPartner}
        searchResults={partnershipSearchResults}
        isSearching={isPartnershipSearchLoading}
        searchError={partnershipSearchError}
        isSaving={isPartnershipSaving}
        saveError={partnershipSaveError}
        onClose={closePartnershipModal}
        onSearchQueryChange={setPartnershipPartnerQuery}
        onSelectPartner={selectPartnershipPartner}
        onSave={savePartnershipPartner}
        onUnlink={unlinkPartnershipPartner}
      />

      <ProfileEmailChangeModal
        isOpen={isEmailModalOpen}
        currentEmail={revealedEmail || profile?.email || ''}
        form={emailForm}
        errors={profileEmailErrors || {}}
        isSaving={isProfileEmailSaving}
        onChange={changeEmailForm}
        onClose={closeEmailModal}
        onSave={submitEmailChange}
      />

      <ProfileEmailRevealModal
        isOpen={isEmailRevealModalOpen}
        maskedEmail={profile?.email || ''}
        form={emailRevealForm}
        errors={profileEmailRevealErrors || {}}
        isSaving={isProfileEmailRevealSaving}
        onChange={changeEmailRevealForm}
        onClose={closeEmailRevealModal}
        onSave={submitEmailReveal}
      />

      <ProfileEmailRevealModal
        isOpen={isPhoneRevealModalOpen}
        title={t('profiles.revealPhone')}
        currentLabel={t('profiles.currentPhone')}
        currentValue={profile?.phoneMasked || ''}
        reasonLabel={t('profiles.phoneRevealReason')}
        savingLabel={t('profiles.revealingPhone')}
        submitLabel={t('profiles.revealPhone')}
        currentInputId="profile-phone-reveal-current"
        reasonInputId="profile-phone-reveal-reason"
        form={phoneRevealForm}
        errors={profilePhoneRevealErrors || {}}
        isSaving={isProfilePhoneRevealSaving}
        onChange={changePhoneRevealForm}
        onClose={closePhoneRevealModal}
        onSave={submitPhoneReveal}
      />

      {isCreateMailOpen ? (
        <InternalMailCreateModal
          form={createMailHook.form}
          formErrors={createMailHook.formErrors}
          isSubmitting={createMailHook.isSubmitting}
          submitError={createMailHook.submitError}
          onFormChange={createMailHook.onFormChange}
          onSubmit={createMailHook.submit}
          onClose={() => setIsCreateMailOpen(false)}
          hideTargeting
        />
      ) : null}

      {selectedDocument ? (
        <DocumentDetailsModal
          selectedDocument={selectedDocument}
          documentViewer={documentViewer}
          documentForm={documentForm}
          onDocumentFormChange={(partial) => setDocumentForm((prev) => ({ ...prev, ...partial }))}
          isDocumentSaving={isDocumentSaving}
          documentSaveError={documentSaveError}
          documentSaveSuccess={documentSaveSuccess}
          isDocumentDownloading={isDocumentDownloading}
          documentDownloadError={documentDownloadError}
          documentStatusOptions={DOCUMENT_STATUS_OPTIONS}
          onDownload={downloadSelectedDocument}
          onClose={handleDocumentClose}
          onSave={saveSelectedDocument}
        />
      ) : null}
      <SecurityStepUpModal
        isOpen={Boolean(securityStepUp?.isOpen)}
        isSubmitting={Boolean(securityStepUp?.isSubmitting)}
        error={securityStepUp?.error || ''}
        onSubmit={submitSecurityStepUpCode}
        onCancel={cancelSecurityStepUp}
      />
      <SecurityStepUpModal
        isOpen={documentSecurityStepUp.isOpen}
        isSubmitting={documentSecurityStepUp.isSubmitting}
        error={documentSecurityStepUp.error}
        onSubmit={submitDocumentSecurityStepUp}
        onCancel={closeDocumentSecurityStepUp}
      />
    </div>
  );
}

export default ProfileDetailsPageView;
